import { TargetSchema, type ClaimItemRowForecast, type ExtractedRecord, type PolicyWindows } from "@/types";
import { DATE_FORMATS, formatDate } from "@/utils/formatters/dateFormatter";
import { getRecordData } from "./recordUtils";
import { parseMoneyCell } from "./amounts";

/**
 * Claim lines outside the policy dates (#7436).
 *
 * The projection judges every claim line against the loader's two windows —
 * paid_date against the policy term, service date against the specific
 * benefit's incurred period — and reports the verdict per row
 * (`ClaimItemRowForecast.counts_toward_deductible` / `ineligible_reasons`)
 * and in aggregate (the `items_outside_policy_period` advisory's `groups`).
 * These helpers turn that into the words the rail card, the Claim lines tab
 * and the expanded claimant panel use. A line that fails both checks is
 * reported under its FIRST reason (paid), which is how the server groups it.
 */

export const OUTSIDE_POLICY_KIND = "items_outside_policy_period";

export type OutsidePolicyReason =
  | "paid_date_outside_policy"
  | "service_date_outside_incurred_period"
  | "not_paid"
  | "missing_paid_amount"
  | "missing_paid_date"
  | "missing_service_date";

/**
 * The server's group order: the two window checks, then the basis engine's
 * not-paid (the status the load will store is not `paid`) and "cannot place
 * in time" reasons.
 */
export const OUTSIDE_POLICY_REASON_ORDER: OutsidePolicyReason[] = [
  "paid_date_outside_policy",
  "service_date_outside_incurred_period",
  "not_paid",
  "missing_paid_amount",
  "missing_paid_date",
  "missing_service_date",
];

const WINDOW_REASONS = new Set<OutsidePolicyReason>([
  "paid_date_outside_policy",
  "service_date_outside_incurred_period",
]);
export const isWindowReason = (reason: OutsidePolicyReason) => WINDOW_REASONS.has(reason);

/** Labels for the reasons that are not a date-vs-window question. */
const MISSING_LABEL: Record<string, string> = {
  not_paid: "Not paid",
  missing_paid_amount: "No paid amount",
  missing_paid_date: "No paid date",
  missing_service_date: "No service date",
};

/** Which side of the window the date fell on; null when the date or window is unknown. */
export type WindowSide = "before" | "after" | null;

export const primaryOutsideReason = (forecast: ClaimItemRowForecast | undefined): OutsidePolicyReason | null => {
  if (!forecast || forecast.counts_toward_deductible !== false) return null;
  const r = forecast.ineligible_reasons?.[0];
  return (OUTSIDE_POLICY_REASON_ORDER as string[]).includes(r ?? "") ? (r as OutsidePolicyReason) : null;
};

const windowName = (reason: OutsidePolicyReason) =>
  reason === "paid_date_outside_policy" ? "paid period" : "incurred period";

/** "before paid period" / "after incurred period" / "outside paid period" (side unknown); "no paid date" for the missing-field reasons. */
export const outsideWindowPhrase = (reason: OutsidePolicyReason, side: WindowSide): string =>
  isWindowReason(reason) ? `${side ?? "outside"} ${windowName(reason)}` : MISSING_LABEL[reason].toLowerCase();

/** Group label on the rail card: "Paid before paid period", "Service date before incurred period", "No paid date". */
export const outsidePolicyGroupLabel = (reason: OutsidePolicyReason, side: WindowSide): string =>
  isWindowReason(reason)
    ? `${reason === "paid_date_outside_policy" ? "Paid" : "Service date"} ${outsideWindowPhrase(reason, side)}`
    : MISSING_LABEL[reason];

const toIsoDay = (v: unknown): string | null => {
  if (v === null || v === undefined || v === "") return null;
  const s = String(v).trim();
  const iso = /^(\d{4}-\d{2}-\d{2})/.exec(s);
  if (iso) return iso[1];
  const us = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(s);
  if (us) return `${us[3]}-${us[1].padStart(2, "0")}-${us[2].padStart(2, "0")}`;
  return null;
};

const sideOf = (isoDay: string | null, start?: string, end?: string): WindowSide => {
  if (!isoDay) return null;
  if (start && isoDay < start) return "before";
  if (end && isoDay > end) return "after";
  return null;
};

export interface OutsidePolicyLine {
  reason: OutsidePolicyReason;
  side: WindowSide;
  /** "Dec 20, 2025" — the date that failed, when the record has it. */
  dateLabel: string | null;
  paidAmount: number | null;
}

/**
 * The failed check for a claim line, with the date that failed it and which
 * side of the window it fell on. Null when the line counts.
 */
export const outsidePolicyLineFor = (
  record: ExtractedRecord,
  forecast: ClaimItemRowForecast | undefined,
  windows: PolicyWindows | null | undefined
): OutsidePolicyLine | null => {
  const reason = primaryOutsideReason(forecast);
  if (!reason) return null;
  const d = getRecordData(record, TargetSchema.CLAIM_ITEM);
  const raw = reason === "paid_date_outside_policy" ? d.paid_date : (d.date_of_service ?? d.date_of_service_start);
  const iso = isWindowReason(reason) ? toIsoDay(raw) : null;
  const side =
    reason === "paid_date_outside_policy"
      ? sideOf(iso, windows?.paid_start, windows?.paid_end)
      : reason === "service_date_outside_incurred_period"
        ? sideOf(iso, windows?.incurred_start, windows?.incurred_end)
        : null;
  return {
    reason,
    side,
    dateLabel: iso ? formatDate(iso, DATE_FORMATS.DISPLAY_LONG) : null,
    paidAmount: parseMoneyCell(d.paid_amount),
  };
};

/** Row sub-line on the Claim lines tab: "Paid Dec 20, 2025 · before paid period"; "No paid date" for a missing field. */
export const outsidePolicyLineText = (line: OutsidePolicyLine): string =>
  isWindowReason(line.reason)
    ? `${line.reason === "paid_date_outside_policy" ? "Paid" : "Service"}${line.dateLabel ? ` ${line.dateLabel}` : ""} · ${outsideWindowPhrase(line.reason, line.side)}`
    : MISSING_LABEL[line.reason];

/** "Incurred Jan 1 – Dec 31, 2026 · Paid Jan 1 – Dec 31, 2026" for the rail card. */
export const policyWindowsLabel = (windows: PolicyWindows | null | undefined): string | null => {
  if (!windows) return null;
  const span = (a?: string, b?: string) =>
    a && b ? `${formatDate(a, DATE_FORMATS.DISPLAY_LONG)} – ${formatDate(b, DATE_FORMATS.DISPLAY_LONG)}` : null;
  const parts: string[] = [];
  if (windows.incurred_check_applies) {
    // No lookback (N = 999): the window has an end and no start worth printing.
    const incurred =
      !windows.incurred_start && windows.incurred_end
        ? `through ${formatDate(windows.incurred_end, DATE_FORMATS.DISPLAY_LONG)}`
        : span(windows.incurred_start, windows.incurred_end);
    if (incurred) parts.push(`Incurred ${incurred}`);
  }
  const paid = span(windows.paid_start, windows.paid_end);
  if (paid) parts.push(`Paid ${paid}`);
  return parts.length ? parts.join(" · ") : null;
};

export interface OutsidePolicyGroup {
  reason: OutsidePolicyReason;
  label: string;
  /** Lines still in play (not excluded). */
  count: number;
  /** Their paid total; null when a row's amount could not be read. */
  amount: number | null;
  /** Loaded record indices in the group, exclusion-aware — what Show in grid and Exclude act on. */
  recordIndices: number[];
}

/**
 * The card's groups, built from the loaded rows' forecasts so Show in grid and
 * Exclude N act on real records and the counts fall as lines are excluded.
 * The server's `groups` (whole projected set) are the fallback for counts
 * and amounts when a line is in the projection but not in the loaded page.
 */
export const buildOutsidePolicyGroups = ({
  records,
  forecastFor,
  isExcluded,
  windows,
  serverGroups,
}: {
  records: ExtractedRecord[];
  forecastFor: (record: ExtractedRecord) => ClaimItemRowForecast | undefined;
  isExcluded: (record: ExtractedRecord) => boolean;
  windows: PolicyWindows | null | undefined;
  serverGroups?: Array<{ reason: string; count: number; amount: string }> | null;
}): OutsidePolicyGroup[] => {
  type Acc = {
    count: number;
    amount: number | null;
    indices: number[];
    sides: Set<string>;
    excludedCount: number;
    excludedAmount: number;
  };
  const acc = new Map<OutsidePolicyReason, Acc>();
  for (const record of records) {
    const line = outsidePolicyLineFor(record, forecastFor(record), windows);
    if (!line) continue;
    const a = acc.get(line.reason) ?? {
      count: 0,
      amount: 0,
      indices: [],
      sides: new Set(),
      excludedCount: 0,
      excludedAmount: 0,
    };
    acc.set(line.reason, a);
    if (isExcluded(record)) {
      a.excludedCount += 1;
      a.excludedAmount += line.paidAmount ?? 0;
      continue;
    }
    a.count += 1;
    a.amount = a.amount === null || line.paidAmount === null ? null : a.amount + line.paidAmount;
    if (typeof record.record_index === "number") a.indices.push(record.record_index);
    if (line.side) a.sides.add(line.side);
  }

  const server = new Map((serverGroups ?? []).map((g) => [g.reason, g]));
  const reasons = new Set<OutsidePolicyReason>([
    ...acc.keys(),
    ...OUTSIDE_POLICY_REASON_ORDER.filter((r) => server.has(r)),
  ]);

  return OUTSIDE_POLICY_REASON_ORDER.filter((r) => reasons.has(r)).flatMap((reason) => {
    const a = acc.get(reason);
    const s = server.get(reason);
    // Whole-set figures from the server when they exceed what is loaded (a
    // paged document whose projection covered more rows than the page).
    const serverCount = s ? s.count - (a?.excludedCount ?? 0) : 0;
    const serverAmount = s ? Number(s.amount) - (a?.excludedAmount ?? 0) : null;
    const count = Math.max(a?.count ?? 0, serverCount);
    const amount =
      a && a.count >= serverCount
        ? a.amount
        : serverAmount !== null && Number.isFinite(serverAmount)
          ? serverAmount
          : null;
    if (count <= 0) return [];
    const sides = a?.sides ?? new Set<string>();
    const side: WindowSide = sides.size === 1 ? ([...sides][0] as WindowSide) : null;
    return [{ reason, label: outsidePolicyGroupLabel(reason, side), count, amount, recordIndices: a?.indices ?? [] }];
  });
};
